import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { SignIn, SignUp, useClerk, useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { setNoAuth } from "@/lib/idb";
import { LOGIN_AS_KEY, loginGroup } from "@/lib/api";
import { Toaster } from "sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import AppShell from "@/components/AppShell";
import { MemberProvider, useMember } from "@/lib/member";
import AuthShell from "@/pages/AuthShell";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Sites from "@/pages/Sites";
import Employers from "@/pages/Employers";
import Vendors from "@/pages/Vendors";
import Workers from "@/pages/Workers";
import WorkerProfile from "@/pages/WorkerProfile";
import Attendance from "@/pages/Attendance";
import Wages from "@/pages/Wages";
import Challans from "@/pages/Challans";
import Compliance from "@/pages/Compliance";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import PrintDoc from "@/pages/PrintDoc";
import MyScore from "./pages/MyScore";
import MyFilings from "./pages/MyFilings";
import Organisation from "./pages/Organisation";
import ContractorProfile from "./pages/ContractorProfile";
import Assistant from "./pages/Assistant";
import PublicConsent from "./pages/PublicConsent";
import ContractorPrint from "./pages/ContractorPrint";
import PeFilings from "@/pages/PeFilings";
import Register from "@/pages/Register";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import SiteVisits from "@/pages/SiteVisits";
import Laws from "@/pages/Laws";
import RaiseLetters from "@/pages/RaiseLetters";
import WageRaises from "@/pages/WageRaises";

const PublicSlips = () => <PrintDoc publicToken={useParams().token} />;
const PublicKits = () => <PrintDoc publicToken={useParams().token} publicDoc="welcome-kit" />;

const Gate = ({ bare }) => {
  const { me } = useMember();
  const { signOut } = useClerk();
  const want = localStorage.getItem(LOGIN_AS_KEY);
  const actual = loginGroup(me?.role);
  const mismatch = !!(me && !me.error && want && actual && want !== actual);
  useEffect(() => {
    if (mismatch) {
      localStorage.removeItem(LOGIN_AS_KEY);
      sessionStorage.setItem("c369-login-wrong", JSON.stringify({ wrong: actual, picked: want }));
      signOut({ redirectUrl: `/sign-in?wrong=${actual}&picked=${want}` });
    }
  }, [mismatch, actual, want, signOut]);
  if (!me || mismatch) return null;
  if (me.error) return <p className="p-8 text-slate-400">Couldn't load your account. Refresh to try again.</p>;
  if (!me.company) return <Onboarding />;
  return bare ? <Outlet /> : <AppShell />;
};

const STARTED_OFFLINE = !navigator.onLine;

const useOnline = () => {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
};

// Signed in normally, or (no signal) a phone that signed in before: the app opens from what's saved on it.
const Protected = ({ bare }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const online = useOnline();
  const signedIn = isLoaded && isSignedIn;
  const offline = !bare && !!localStorage.getItem("c369-me") && (!online || (STARTED_OFFLINE && !signedIn));
  setNoAuth(offline && !signedIn);
  useEffect(() => { if (signedIn && STARTED_OFFLINE) window.dispatchEvent(new Event("online")); }, [signedIn]);
  if (signedIn || offline) return <MemberProvider><Gate bare={bare} /></MemberProvider>;
  return isLoaded ? <Navigate to="/sign-in" replace /> : null;
};

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthShell picker><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthShell>} />
          <Route path="/sign-up/*" element={<AuthShell><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthShell>} />
          <Route path="/slips/:token" element={<PublicSlips />} />
          <Route path="/kits/:token" element={<PublicKits />} />
          <Route path="/consent/:token" element={<PublicConsent />} />
          <Route element={<Protected bare />}>
            <Route path="/print/:doc" element={<PrintDoc />} />
            <Route path="/print-contractor/:vid" element={<ContractorPrint />} />
            <Route path="/print-raise/:state/:vid" element={<RaiseLetters />} />
          </Route>
          <Route element={<Protected />}>
            <Route path="/" element={<Home />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/employers" element={<Employers />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/workers" element={<Workers />} />
            <Route path="/workers/:id" element={<WorkerProfile />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/wages" element={<Wages />} />
            <Route path="/challans" element={<Challans />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/register" element={<Register />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/pe" element={<PeFilings />} />
            <Route path="/my-score" element={<MyScore />} />
            <Route path="/my-filings" element={<MyFilings />} />
            <Route path="/organisation" element={<Organisation />} />
            <Route path="/contractors/:vid" element={<ContractorProfile />} />
            <Route path="/assistant" element={<Assistant />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/site-visits" element={<SiteVisits />} />
            <Route path="/laws" element={<Laws />} />
            <Route path="/raises" element={<WageRaises />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-right" richColors />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
